/**
 * 站台指標的共用顯示邏輯（批112）。對照 `lib/quotas.ts`——那邊翻的是產品宣告的配額鍵，
 * 這邊翻的是**產品回報上來**的指標鍵。
 *
 * ★ 兩件事刻意與 `quotas.ts` 不同：
 *
 * 1. **查不到定義不是例外，是常態**。指標鍵由產品端自己決定，我方的顯示定義是人工補的
 *    ——新產品送了一個沒人定義過的鍵是必然會發生的事。所以 `metricLabel` 查不到就回
 *    原始 key，`buildMetricRows` 把那些鍵標成 `unknown` 排在後面，畫面提示去補定義。
 * 2. **沒有「上限」這個維度**。指標是純觀測值，不參與任何額度判斷，所以沒有百分比、
 *    沒有色階、沒有 over 旗標。
 */
import type { MetricDefinition } from '@/api/ls'

/** 心跳回報的指標快照。 */
export type Metrics = Record<string, number> | null | undefined

function defOf(
  defs: MetricDefinition[] | null | undefined,
  product: string | null | undefined,
  key: string,
): MetricDefinition | null {
  if (!product) return null
  return (defs ?? []).find((d) => d.product === product && d.key === key) ?? null
}

/** 指標鍵 → 中文名（查不到／沒有產品＝回原始 key，不猜、不擲錯）。 */
export function metricLabel(
  defs: MetricDefinition[] | null | undefined,
  product: string | null | undefined,
  key: string,
): string {
  return defOf(defs, product, key)?.label ?? key
}

/**
 * 依顯示定義格式化一個值。`def` 為 null（未定義的鍵）＝原樣印，最多六位小數
 * ——**不四捨五入成整數**，那會讓「0.004」變成「0」，看起來像沒在用。
 */
export function fmtMetric(def: MetricDefinition | null | undefined, value: number): string {
  if (!Number.isFinite(value)) return '—'
  if (!def) {
    const rough = Math.abs(value) >= 1000 || Number.isInteger(value) ? 0 : 6
    return trimZeros(value.toFixed(rough))
  }
  const decimals = def.value_format === 'integer' ? 0 : Math.max(0, Math.min(6, def.decimals ?? 2))
  // ★ `percent` 只是**加一個百分號**，不做 ×100：產品端回報的就是百分比數值本身
  //   （LS 不解讀語意，也就沒有立場替它換算）。
  const text = value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  const suffix = def.value_format === 'percent' ? '%' : def.unit ? ` ${def.unit}` : ''
  return `${text}${suffix}`
}

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s
}

export interface MetricRow {
  key: string
  label: string
  value: number
  /** 已格式化的顯示字串（含單位／百分號）。 */
  text: string
  hint: string | null
  /** 這個鍵在 `metric_definitions` 裡沒有對應列——畫面上摺疊起來並提供「建立顯示定義」。 */
  unknown: boolean
}

/**
 * 把一包 `{key: number}` 攤成可直接 render 的列。
 *
 * 順序：**已定義的依 `sort_order`（同序再依 key）在前、未定義的字母序在後**。
 * `hidden` 的定義整列濾掉（收得到但不顯示）；未定義的鍵**永遠不濾**——營運要看得到
 * 「有一個沒人定義過的鍵進來了」，否則永遠不會有人去補定義。
 */
export function buildMetricRows(
  defs: MetricDefinition[] | null | undefined,
  product: string | null | undefined,
  metrics: Metrics,
): MetricRow[] {
  const known: MetricRow[] = []
  const unknown: MetricRow[] = []
  for (const [key, value] of Object.entries(metrics ?? {})) {
    if (typeof value !== 'number') continue
    const def = defOf(defs, product, key)
    if (def?.hidden) continue
    const row: MetricRow = {
      key,
      label: def?.label ?? key,
      value,
      text: fmtMetric(def, value),
      hint: def?.hint ?? null,
      unknown: def == null,
    }
    ;(def ? known : unknown).push(row)
  }
  known.sort((a, b) => {
    const sa = defOf(defs, product, a.key)?.sort_order ?? 0
    const sb = defOf(defs, product, b.key)?.sort_order ?? 0
    return sa - sb || a.key.localeCompare(b.key)
  })
  unknown.sort((a, b) => a.key.localeCompare(b.key))
  return [...known, ...unknown]
}
